import Divider from 'components/shared/others/divider/divider';

import {
  BAR_MAX_HEIGHT,
  VALUE_LABEL_SPACE,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/components/constants';
import {
  formatNumber,
  renderChangeValue,
  pluralizeMetric,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/components/metric-block.helpers';

interface MetricBlockProps {
  metricKey: string;
  unit?: string;
  primary?: number | null;
  secondary?: number | null;
  primaryLabel?: string | number;
  secondaryLabel?: string | number;
  change?: number | string | null;
  cohortChange?: number | string | null;
  isRate?: boolean;
  cohortName?: string;
}

interface ColumnArgs {
  value: number | null | undefined;
  height: number;
  colorClass: string;
  label: string | number | undefined;
}

const MetricBlock = ({
  metricKey,
  unit,
  primary,
  secondary,
  primaryLabel,
  secondaryLabel,
  change,
  cohortChange,
  isRate,
  cohortName = 'Cohort',
}: MetricBlockProps): React.JSX.Element => {
  const primaryVal = primary;
  const secondaryVal = secondary;

  const max = Math.max(primaryVal || 0, secondaryVal || 0, 1);
  const barHeight1 = ((primaryVal as number) / max) * BAR_MAX_HEIGHT;
  const barHeight2 = ((secondaryVal as number) / max) * BAR_MAX_HEIGHT;

  const renderColumn = ({
    value,
    height,
    colorClass,
    label,
  }: ColumnArgs): React.JSX.Element => (
    <div className="text-center flex flex-col justify-end px-2.5 w-full">
      <div
        style={{ height: `${BAR_MAX_HEIGHT + VALUE_LABEL_SPACE}px` }}
        className="relative w-full overflow-hidden"
      >
        <div
          className={`${colorClass} transition-all duration-700 ease-out absolute bottom-0 left-0 right-0`}
          style={{ height: `${height}px` }}
        />
        <span
          className="absolute left-1/2 -translate-x-1/2 text-xs font-semibold text-neutral-950"
          style={{ bottom: height + 4 }}
        >
          {formatNumber(value, unit)}
          {unit}
        </span>
      </div>
      <p className="text-xs mt-1 text-gray-500">{label}</p>
    </div>
  );

  const renderChangeRow = (
    label: string,
    value: number | string | null | undefined
  ): React.JSX.Element => (
    <div className="flex items-baseline justify-between gap-2 min-w-0">
      <div className="text-gray-500 flex-1" title={label}>
        {label}
      </div>
      <div className="font-semibold text-gray-700 text-sm whitespace-nowrap shrink-0">
        {renderChangeValue(value)}%
      </div>
    </div>
  );

  const rawMetricType = metricKey.replace(/([A-Z])/g, ' $1').trim();

  const metricType = pluralizeMetric(rawMetricType);

  return (
    <div className="text-center">
      <div className="text-center font-semibold text-gray-700 mb-2 capitalize px-2.5">
        {metricType}
      </div>

      <div className="flex justify-between items-end">
        {renderColumn({
          value: primaryVal,
          height: barHeight1,
          colorClass: 'bg-blue-700',
          label: primaryLabel,
        })}
        {renderColumn({
          value: secondaryVal,
          height: barHeight2,
          colorClass: 'bg-blue-800',
          label: secondaryLabel,
        })}
      </div>

      <Divider customClass="my-2" />
      <div className="mt-4 text-xs text-left text-gray-500 font-normal flex flex-col gap-1 px-2.5">
        {renderChangeRow(`My School ${isRate ? 'rate' : 'change'}`, change)}
        {renderChangeRow(
          `${cohortName} ${isRate ? 'rate' : 'change'}`,
          cohortChange
        )}
      </div>
    </div>
  );
};

export default MetricBlock;
